import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import logo from "../../assets/images/logo_blanc.png";
import Memo from "../../assets/docs/Memo_d_utilisation_juil_2026.pdf";
import MemoSepteo from "../../assets/docs/memo_d_utilisation_septeo.pdf";
import LoyiaInlineIcon from "../../views/loyia/LoyiaInlineIcon";
import Mail from "../../components/mail/mail";
import HeaderSearch from "./HeaderSearch";
import "./header.css";

const hasAuthenticatedUser = () => {
  try {
    return Boolean(JSON.parse(localStorage.getItem("user"))?.jwt);
  } catch {
    return false;
  }
};

const Header = () => {
  const history = useHistory();
  const location = useLocation();
  const headerRef = useRef(null);
  const [isRemarksOpen, setIsRemarksOpen] = useState(false);
  const [openHeaderMenu, setOpenHeaderMenu] = useState(null);
  const [isHeaderNavOpen, setIsHeaderNavOpen] = useState(false);
  const isAuthenticated = hasAuthenticatedUser();

  const closeHeaderDropdown = () => {
    setOpenHeaderMenu(null);
    setIsHeaderNavOpen(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const toggleHeaderDropdown = (menuKey) => {
    setOpenHeaderMenu((currentMenuKey) => (currentMenuKey === menuKey ? null : menuKey));
  };

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("remarque");
    localStorage.removeItem("currentEmol");
    localStorage.removeItem("chart");
    setOpenHeaderMenu(null);
    setIsHeaderNavOpen(false);
    history.replace("/login");
  };

  const navigateTo = (path) => {
    closeHeaderDropdown();
    setIsHeaderNavOpen(false);
    history.push(path);
  };

  const openRemarks = () => {
    closeHeaderDropdown();
    setIsRemarksOpen(true);
  };

  const handleLogoClick = () => {
    if (isAuthenticated) {
      navigateTo("/");
    } else {
      navigateTo("/login");
    }
  };

  const getHeaderLinkClassName = (...paths) =>
    paths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
      ? "navLink is-active"
      : "navLink";

  const isMenuToggleActive = isHeaderNavOpen || ["/favoris", "/moncompte", "/loyia"]
    .some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!headerRef.current) {
        return;
      }

      const clickedInsideHeader = headerRef.current.contains(event.target);
      const clickedNav = event.target.closest("#header-main-nav");
      const clickedMenuToggle = event.target.closest(".headerMenuToggle");

      if (!clickedInsideHeader || (!clickedNav && !clickedMenuToggle)) {
        setOpenHeaderMenu(null);
        setIsHeaderNavOpen(false);
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="siteHeader" ref={headerRef}>
      <div className={`headerInner ${isAuthenticated ? "" : "headerInner--guest"}`}>
        <div className="headerLogo" onClick={handleLogoClick} aria-label="Accueil">
          <img src={logo} alt="Éditions Langloÿs" />
        </div>

        <button
          type="button"
          className={`headerMenuToggle ${isMenuToggleActive ? "is-active" : ""}`}
          aria-expanded={isHeaderNavOpen}
          aria-controls="header-main-nav"
          onClick={() => {
            setOpenHeaderMenu(null);
            setIsHeaderNavOpen((currentValue) => !currentValue);
          }}
        >
          Menu
        </button>

        <nav
          id="header-main-nav"
          className={`headerNav ${isHeaderNavOpen ? "is-open" : ""}`}
          aria-label="Navigation principale"
        >
          <div className={`navItem ${openHeaderMenu === "editions" ? "is-open" : ""}`}>
            <button
              type="button"
              className="navLink"
              aria-expanded={openHeaderMenu === "editions"}
              onClick={() => toggleHeaderDropdown("editions")}
            >
              Éditions Langloÿs
            </button>
            <div className="navDropdown navDropdown--compact">
              <ul className="navDropdownList">
                <li>
                  <a
                    href="https://editionslangloys.com/nos-produits"
                    target="_blank"
                    rel="noreferrer"
                    onClick={closeHeaderDropdown}
                  >
                    Nos produits
                  </a>
                </li>
                <li>
                  <a
                    href="https://editionslangloys.com"
                    target="_blank"
                    rel="noreferrer"
                    onClick={closeHeaderDropdown}
                  >
                    Qui sommes-nous
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {isAuthenticated ? (
            <div className="navItem">
              <Link
                to="/favoris"
                className={getHeaderLinkClassName("/favoris")}
                onClick={() => {
                  closeHeaderDropdown();
                  setIsHeaderNavOpen(false);
                }}
              >
                Mes favoris
              </Link>
            </div>
          ) : null}

          <div className={`navItem ${openHeaderMenu === "aide" ? "is-open" : ""}`}>
            <button
              type="button"
              className="navLink"
              aria-expanded={openHeaderMenu === "aide"}
              onClick={() => toggleHeaderDropdown("aide")}
            >
              Aide
            </button>
            <div className="navDropdown navDropdown--compact">
              <ul className="navDropdownList">
                <li>
                  <a href={Memo} target="_blank" rel="noreferrer" onClick={closeHeaderDropdown}>
                    Mémo d'utilisation
                  </a>
                </li>
                <li>
                  <a href={MemoSepteo} target="_blank" rel="noreferrer" onClick={closeHeaderDropdown}>
                    Mémo Septeo
                  </a>
                </li>
                {isAuthenticated ? (
                  <li>
                    <button type="button" className="navDropdownItem" onClick={openRemarks}>
                      Vos remarques
                    </button>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>

          {isAuthenticated ? (
            <div className={`navItem ${openHeaderMenu === "compte" ? "is-open" : ""}`}>
              <button
                type="button"
                className={getHeaderLinkClassName("/moncompte")}
                aria-expanded={openHeaderMenu === "compte"}
                onClick={() => toggleHeaderDropdown("compte")}
              >
                Mon compte
              </button>
              <div className="navDropdown navDropdown--compact">
                <ul className="navDropdownList">
                  <li>
                    <button
                      type="button"
                      className="navDropdownItem"
                      onClick={() => {
                        closeHeaderDropdown();
                        navigateTo("/moncompte");
                      }}
                    >
                      Espace personnel
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="navDropdownItem"
                      onClick={() => {
                        closeHeaderDropdown();
                        logout();
                      }}
                    >
                      Se déconnecter
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          ) : null}

          <div className="navItem navItem--loyia">
                        <Link
              to="/loyia"
              className={`${getHeaderLinkClassName("/loyia")} navLink--loyia`}
              onClick={() => {
                closeHeaderDropdown();
                setIsHeaderNavOpen(false);
              }}
            >
              <span>LOYIA</span>
              <LoyiaInlineIcon size={42} className="loyiaAvatar" />
            </Link>
          </div>
        </nav>

        {isAuthenticated ? (
          <div className="headerActions">
            <HeaderSearch />
          </div>
        ) : null}
      </div>
      {isAuthenticated ? (
        <Mail state={isRemarksOpen} setOpen={setIsRemarksOpen} />
      ) : null}
    </header>
  );
};

export default Header;
